'use client';

// "Invite" for chat rooms — WhatsApp / Telegram / copy link / native share.
// The link is resolved lazily on open (a group invite needs an RPC to mint the
// token; public rooms are just their URL). Whoever opens the link is routed
// through sign-in if needed and lands straight in the chat.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Send, Share2, UserPlus, X } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';
import { track } from '@/lib/analytics';

function WhatsAppIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.48-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35M12.05 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.89 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.59 5.95L0 24l6.3-1.65a11.9 11.9 0 0 0 5.74 1.46h.01c6.55 0 11.89-5.33 11.89-11.9 0-3.18-1.24-6.17-3.48-8.41" />
    </svg>
  );
}

export default function InviteButton({
  getUrl,
  message,
  label,
  compact = false,
  note,
}: {
  /** Resolve the share URL (may mint a token). Called each time the popover opens. */
  getUrl: () => Promise<string> | string;
  /** Message text put in front of the link on WhatsApp/Telegram. */
  message: string;
  label?: string;
  /** Icon-only trigger (group header). */
  compact?: boolean;
  /** Extra caveat shown under the description — e.g. when the link doesn't grant access by itself. */
  note?: string;
}) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setError('');
    setCopied(false);
    Promise.resolve()
      .then(() => getUrl())
      .then((u) => alive && setUrl(u))
      .catch((e: any) => alive && setError(e?.message || t('Could not create the invite link.')));
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      alive = false;
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fullText = url ? `${message}\n${url}` : message;

  const onCopy = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(true);
    track('chat_invite_share', { channel: 'copy' });
    setTimeout(() => setCopied(false), 1800);
  }, [url]);

  const onNative = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.share({ title: 'ShowMeTrade', text: message, url });
      track('chat_invite_share', { channel: 'native' });
      setOpen(false);
    } catch { /* user dismissed */ }
  }, [url, message]);

  const canNative = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const rowCls = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-colors duration-150 disabled:opacity-40';
  const rowStyle = { color: 'var(--text-primary)' } as const;
  const hover = (e: React.MouseEvent<HTMLElement>, on: boolean) => {
    (e.currentTarget as HTMLElement).style.background = on ? 'var(--bg-sunken)' : 'transparent';
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={compact
          ? 'p-1 rounded-lg transition-colors duration-150'
          : 'flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors duration-150'}
        style={compact ? { color: 'var(--text-secondary)' } : { background: 'var(--accent)', color: '#ffffff' }}
        title={t('Invite to chat')}
        aria-expanded={open}
      >
        <UserPlus size={compact ? 17 : 15} />
        {!compact ? <span>{label ?? t('Invite')}</span> : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full mt-2 w-[260px] p-2 z-[60] rounded-2xl border shadow-xl"
          style={{ background: 'var(--bg-card)', borderColor: 'var(--border)', boxShadow: '0 16px 40px rgba(28,43,38,0.18)' }}
          role="dialog"
        >
          <div className="flex items-start justify-between gap-2 px-2 pt-1 pb-2">
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('Invite to chat')}</p>
              <p className="text-[11.5px] mt-0.5 leading-snug" style={{ color: 'var(--text-muted)' }}>
                {t('Send the link — whoever opens it joins the chat.')}
              </p>
              {note && (
                <p className="text-[11.5px] mt-1 leading-snug font-semibold" style={{ color: 'var(--accent)' }}>
                  {note}
                </p>
              )}
            </div>
            <button onClick={() => setOpen(false)} className="p-1 -mr-1 -mt-1" title={t('Close')}>
              <X size={14} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>

          {error ? (
            <p className="text-xs px-2 pb-2" style={{ color: 'var(--bearish)' }}>{error}</p>
          ) : null}

          <a
            href={url ? `https://wa.me/?text=${encodeURIComponent(fullText)}` : undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!url}
            onClick={(e) => { if (!url) e.preventDefault(); else { track('chat_invite_share', { channel: 'whatsapp' }); setOpen(false); } }}
            className={rowCls}
            style={rowStyle}
            onMouseEnter={(e) => hover(e, true)}
            onMouseLeave={(e) => hover(e, false)}
          >
            <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: '#25D366', color: '#fff' }}>
              <WhatsAppIcon size={16} />
            </span>
            WhatsApp
          </a>

          <a
            href={url ? `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}` : undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!url}
            onClick={(e) => { if (!url) e.preventDefault(); else { track('chat_invite_share', { channel: 'telegram' }); setOpen(false); } }}
            className={rowCls}
            style={rowStyle}
            onMouseEnter={(e) => hover(e, true)}
            onMouseLeave={(e) => hover(e, false)}
          >
            <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: '#26A5E4', color: '#fff' }}>
              <Send size={15} />
            </span>
            Telegram
          </a>

          <button
            type="button"
            onClick={onCopy}
            disabled={!url}
            className={rowCls}
            style={rowStyle}
            onMouseEnter={(e) => hover(e, true)}
            onMouseLeave={(e) => hover(e, false)}
          >
            <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
              {copied ? <Check size={15} /> : <Copy size={15} />}
            </span>
            {copied ? t('Link copied') : t('Copy link')}
          </button>

          {canNative ? (
            <button
              type="button"
              onClick={onNative}
              disabled={!url}
              className={rowCls}
              style={rowStyle}
              onMouseEnter={(e) => hover(e, true)}
              onMouseLeave={(e) => hover(e, false)}
            >
              <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)' }}>
                <Share2 size={15} />
              </span>
              {t('More…')}
            </button>
          ) : null}

          {url ? (
            <p className="text-[11px] px-2 pt-2 pb-1 truncate font-mono-nums" style={{ color: 'var(--text-muted)' }} title={url}>
              {url.replace(/^https?:\/\//, '')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
